import React, { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CheckIcon, SearchIcon, XIcon } from "lucide-react";
import { Input } from "../ui/input";
import { useCommunityPanelStore } from "@/store/zustand/communityPanelStore";
import { cn } from "@/lib/utils";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@radix-ui/react-hover-card";
import { BaseCard } from "./CommunityCard";
import { Checkbox } from "../ui/checkbox";

interface CommunityFilterPopoverProps {
  children: React.ReactNode;
  onFilterChange?: () => void; // Callback when filters are applied/changed
}

export const CommunityFilterPopover: React.FC<CommunityFilterPopoverProps> = ({
  children,
  onFilterChange,
}) => {
  const {
    isFilterPopoverOpen,
    setFilterPopoverOpen,
    selectedTags,
    toggleTag,
    filterSearchVal,
    setFilterSearchVal,
    allCollectionsForFilter,
    setAllCollectionsForFilter,
    filteredCollections,
    setSelectedCollections,
    selectedCollections,
    toggleCollection,
    collections,
  } = useCommunityPanelStore();

  // Update allCollectionsForFilter when collections change
  useEffect(() => {
    if (collections.length > 0) {
      setAllCollectionsForFilter(collections);
    }
  }, [collections, setAllCollectionsForFilter]);

  const handleOpenChange = (open: boolean) => {
    setFilterPopoverOpen(open);
    if (!open) {
      onFilterChange?.();
      // prevent jarring renders while closing
      setTimeout(() => {
        setFilterSearchVal("");
      }, 300);
    }
  };

  return (
    <Popover open={isFilterPopoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-64 p-0 rounded-lg transition-all"
        align="start"
      >
        <div className="flex flex-col h-min">
          {/* Search Input */}
          <div className="p-2">
            <div className="relative group">
              <Input
                placeholder="Search"
                className="w-full pl-9 h-7 bg-secondary-200 text-xs text-secondary-n-50 placeholder:text-text-muted border-0 focus-within:border-primary-0 focus-within:border transition"
                value={filterSearchVal}
                onChange={(e) => setFilterSearchVal(e.target.value)}
              />
              <SearchIcon
                className={
                  "absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 text-text-muted group-focus-within:text-primary-0 transition"
                }
              />
              {filterSearchVal && (
                <button
                  type="button"
                  className="absolute top-1/2 right-3 -translate-y-1/2 text-secondary-500 hover:text-secondary-n-50"
                  onClick={() => setFilterSearchVal("")}
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Platform Filters */}
          <div className="p-2 border-y border-secondary-300">
            <div className="space-y-1">
              <div
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-xs hover:bg-background-tertiary"
                )}
                onClick={() => {
                  toggleTag("web");
                  onFilterChange?.();
                }}
              >
                <Checkbox
                  id="web"
                  checked={selectedTags.includes("web")}
                  className="h-4 w-4 rounded-sm data-[state=checked]:bg-[#4B4B51]"
                  style={{
                    border: "1px solid var(--border-light)",
                  }}
                />
                <label htmlFor="web" className="text-xs pointer-events-none">
                  Web
                </label>
              </div>
              <div
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-xs hover:bg-background-tertiary"
                )}
                onClick={() => {
                  toggleTag("mobile");
                  onFilterChange?.();
                }}
              >
                <Checkbox
                  id="mobile"
                  checked={selectedTags.includes("mobile")}
                  className="h-4 w-4 rounded-sm data-[state=checked]:bg-[#4B4B51]"
                  style={{
                    border: "1px solid var(--border-light)",
                  }}
                />
                <label htmlFor="mobile" className="text-xs pointer-events-none">
                  Mobile
                </label>
              </div>
            </div>
          </div>

          <p className="p-2 text-xs font-medium text-secondary-n-300 select-none">
            <div className="space-y-1">
              {filteredCollections.length > 0 ? (
                filteredCollections.map((collection) => (
                  <HoverCard
                    key={collection.id}
                    openDelay={300}
                    closeDelay={200}
                  >
                    <HoverCardTrigger asChild>
                      <button
                        className={cn(
                          "flex items-center cursor-pointer hover:bg-background-tertiary p-1 px-2 rounded-md w-full text-left text-text-primary",
                          "transition-colors"
                        )}
                        onClick={() => {
                          if (
                            selectedCollections.includes(
                              collection.id.toString()
                            )
                          ) {
                            setSelectedCollections([]);
                          } else {
                            setSelectedCollections([collection.id.toString()]);
                          }
                          onFilterChange?.();
                        }}
                      >
                        {collection.image_url && (
                          <div className="h-6 w-6 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={collection.image_url}
                              alt={collection.name}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        )}
                        <label
                          htmlFor={`collection-${collection.id}`}
                          className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 truncate cursor-pointer pl-2"
                          title={collection.name}
                        >
                          {collection.name}
                        </label>
                        {selectedCollections.includes(
                          collection.id.toString()
                        ) && <CheckIcon className="w-4 h-4" />}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      side="right"
                      sideOffset={10}
                      className="p-0 bg-transparent border-none shadow-none z-50 pointer-events-none"
                    >
                      <BaseCard
                        // title={collection.name}
                        imageUrl={collection.image_url || undefined}
                        isThumbnail={true}
                        className="w-48"
                      />
                    </HoverCardContent>
                  </HoverCard>
                ))
              ) : filterSearchVal ? (
                <p className="text-xs text-secondary-n-500 text-center py-4">
                  0 search results for "{filterSearchVal}"
                </p>
              ) : (
                <p className="text-xs text-secondary-n-500 text-center py-4">
                  No UI Kits found.
                </p>
              )}
            </div>
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
